import { useCallback, useEffect, useMemo, useState } from "react";

import {
	PERMISSION_SETTINGS,
	WORKER_REQUESTS_PERMISSIONS,
} from "../../../../../../config/permissions";
import axios from "../../../../../../utils/api-client";
import { deCamelCase } from "../../../../../../utils/helpers";

// GAT-7829: which supervisor roles may add past-dated time off at a location,
// and how many days back. Read and saved through
// GET/PUT /rbac/permissions/{code}/locations/{locationId}, which lists only the
// allowed roles; role names and ids come from /Users/RolesForAdmin.

export const MAX_LOOKBACK_DAYS = 36500;

// Only roles that can mark time off on a worker's behalf are offered; the API
// accepts any company role, but allowing others (e.g. ReadOnly) has no effect.
const ELIGIBLE_ROLE_NAMES = [
	"HR Manager",
	"ShiftSupervisor",
	"DepartmentManager",
	"LimitedAccessSupervisor",
];

export type PastTimeOffAccess = "none" | "limited" | "unlimited";

export interface AdminRole {
	id: number;
	name: string;
}

// A role allowed at the location, as the API sends and receives it.
export interface PermissionLocationRole {
	roleId: number;
	roleName?: string;
	// null means no limit.
	settings: Record<string, number | null>;
}

// One role as the form edits it.
export interface PastTimeOffRole {
	roleId: number;
	label: string;
	access: PastTimeOffAccess;
	// Kept as typed so a half-typed value isn't lost.
	days: string;
}

const permissionLocationUrl = (locationId: number | string) =>
	`${process.env.REACT_APP_API_END_POINT}/rbac/permissions/${
		WORKER_REQUESTS_PERMISSIONS.CREATE_PAST_TIME_OFF
	}/locations/${locationId}`;

const toFormRole = (
	roleId: number,
	roleName: string,
	allowedRole: PermissionLocationRole | undefined
): PastTimeOffRole => {
	const lookbackDays =
		allowedRole?.settings?.[PERMISSION_SETTINGS.LOOKBACK_DAYS] ?? null;
	let access: PastTimeOffAccess = "none";
	if (allowedRole) access = lookbackDays === null ? "unlimited" : "limited";
	return {
		roleId,
		// deCamelCase leaves a leading space on names like "ShiftSupervisor".
		label: deCamelCase(roleName).trim(),
		access,
		days: lookbackDays === null ? "" : String(lookbackDays),
	};
};

// The eligible roles, marked allowed when the location's list includes them.
export const buildFormRoles = (
	adminRoles: AdminRole[],
	allowedRoles: PermissionLocationRole[]
): PastTimeOffRole[] => {
	const roleNames = new Map<number, string>();
	adminRoles.forEach((role) => roleNames.set(role.id, role.name));
	// An allowed role still shows even if the admin couldn't assign it.
	allowedRoles.forEach((role) => {
		if (!roleNames.has(role.roleId)) {
			roleNames.set(role.roleId, role.roleName ?? `Role ${role.roleId}`);
		}
	});

	return Array.from(roleNames.entries())
		.filter(([, name]) => ELIGIBLE_ROLE_NAMES.includes(name))
		.map(([roleId, name]) =>
			toFormRole(
				roleId,
				name,
				allowedRoles.find((role) => role.roleId === roleId)
			)
		)
		.sort((a, b) => a.label.localeCompare(b.label));
};

// Not allowed roles are left out; the backend replaces the location's list.
export const toRequestBody = (roles: PastTimeOffRole[]) => ({
	roles: roles
		.filter((role) => role.access !== "none")
		.map((role) => ({
			roleId: role.roleId,
			settings: {
				[PERMISSION_SETTINGS.LOOKBACK_DAYS]:
					role.access === "limited" ? Number(role.days) : null,
			},
		})),
});

export const isValidLookbackDays = (days: string) => {
	if (!/^\d+$/.test(days)) return false;
	const value = Number(days);
	return value >= 1 && value <= MAX_LOOKBACK_DAYS;
};

export const hasInvalidPastTimeOffRole = (roles: PastTimeOffRole[]) =>
	roles.some(
		(role) => role.access === "limited" && !isValidLookbackDays(role.days)
	);

// The backend refuses an empty list; switching the feature off is the way to
// stop everyone at a location.
export const hasNoRoleAllowed = (roles: PastTimeOffRole[]) =>
	roles.length > 0 && roles.every((role) => role.access === "none");

const usePastTimeOffRoles = (locationId: number | string | undefined) => {
	const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);
	const [roles, setRoles] = useState<PastTimeOffRole[]>([]);
	const [newRoles, setNewRoles] = useState<PastTimeOffRole[]>([]);
	const [status, setStatus] = useState<
		"idle" | "pending" | "fulfilled" | "error"
	>("idle");
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!locationId) return;
		setStatus("pending");
		try {
			const [permissionResponse, rolesResponse] = await Promise.all([
				axios.get(permissionLocationUrl(locationId)),
				axios.get(`${process.env.REACT_APP_API_END_POINT}/Users/RolesForAdmin`),
			]);
			const assignableRoles: AdminRole[] = rolesResponse.data ?? [];
			const formRoles = buildFormRoles(
				assignableRoles,
				permissionResponse.data?.roles ?? []
			);
			setAdminRoles(assignableRoles);
			setRoles(formRoles);
			setNewRoles(formRoles);
			setStatus("fulfilled");
		} catch (error) {
			console.error("Failed to load past-dated time off roles", error);
			setStatus("error");
		}
	}, [locationId]);

	useEffect(() => {
		load();
	}, [load]);

	const isChanged = useMemo(
		() => JSON.stringify(roles) !== JSON.stringify(newRoles),
		[roles, newRoles]
	);

	// Unsaved edits stay in the form on failure so the admin can retry.
	const save = useCallback(async () => {
		if (!locationId || !isChanged) return;
		setIsSaving(true);
		setSaveError(null);
		try {
			const response = await axios.put(
				permissionLocationUrl(locationId),
				toRequestBody(newRoles)
			);
			const savedRoles = buildFormRoles(
				adminRoles,
				response.data?.roles ?? []
			);
			setRoles(savedRoles);
			setNewRoles(savedRoles);
		} catch (error: any) {
			console.error("Failed to save past-dated time off roles", error);
			setSaveError(
				error?.response?.data?.message ??
					"Couldn't save the role access. Your changes are still here; try Save again."
			);
		} finally {
			setIsSaving(false);
		}
	}, [locationId, isChanged, newRoles, adminRoles]);

	const reset = useCallback(() => {
		setNewRoles(roles);
		setSaveError(null);
	}, [roles]);

	const updateRole = useCallback(
		(roleId: number, changes: Partial<PastTimeOffRole>) =>
			setNewRoles((previous) =>
				previous.map((role) =>
					role.roleId === roleId ? { ...role, ...changes } : role
				)
			),
		[]
	);

	return {
		roles,
		newRoles,
		status,
		isSaving,
		saveError,
		isChanged,
		hasInvalidRole: hasInvalidPastTimeOffRole(newRoles),
		hasNoRoleAllowed: hasNoRoleAllowed(newRoles),
		updateRole,
		save,
		reset,
		reload: load,
	};
};

export default usePastTimeOffRoles;
